import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth, db } from "../firebase/config";
import { onAuthStateChanged } from "firebase/auth";
import { doc, updateDoc, serverTimestamp, increment } from "firebase/firestore";

const PaymentReturn = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [status, setStatus] = useState("processing");
  const [message, setMessage] = useState("Activation de votre abonnement...");
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    console.log("🚀 PaymentReturn - Début du traitement");
    
    const processPayment = async (user) => {
      try {
        const plan = localStorage.getItem("pendingPlan");
        const sessionId = searchParams.get("session_id");

        console.log("📊 Informations de paiement:");
        console.log("   → Plan:", plan);
        console.log("   → Session ID:", sessionId);
        console.log("   → User UID:", user.uid);

        if (!plan) {
          console.warn("⚠️ Aucun plan en attente");
          setStatus("error");
          setMessage("Aucun plan détecté. Veuillez refaire votre achat.");
          return;
        }

        console.log("🔄 Mise à jour Firestore...");
        const userRef = doc(db, "users", user.uid);

        // ✅ PAIEMENT UNIQUE - Reste en FREE + increment diagnosticsLimit
        if (plan === "single") {
          await updateDoc(userRef, {
            diagnosticsLimit: increment(1),
            lastPaymentDate: serverTimestamp(),
          });
          console.log("✅ Diagnostic unique ajouté (reste en FREE)");
          
          localStorage.removeItem("pendingPlan");
          setStatus("success");
          setMessage("Votre diagnostic unique a été ajouté avec succès !");
          return;
        }

        // ✅ ABONNEMENT SILVER
        if (plan === "silver") {
          await updateDoc(userRef, {
            subscriptionType: "silver",
            diagnosticsLimit: 3,
            subscriptionStatus: "active",
            diagnosticsThisMonth: 0,
            diagnosticsResetDate: serverTimestamp(),
            lastPaymentDate: serverTimestamp(),
          });
          console.log("✅ Abonnement Silver activé");
          
          localStorage.removeItem("pendingPlan");
          setStatus("success");
          setMessage("Votre abonnement Silver a été activé avec succès !");
          return;
        }

        // ✅ ABONNEMENT GOLD
        if (plan === "gold") {
          await updateDoc(userRef, {
            subscriptionType: "gold",
            diagnosticsLimit: 7,
            subscriptionStatus: "active",
            diagnosticsThisMonth: 0,
            diagnosticsResetDate: serverTimestamp(),
            lastPaymentDate: serverTimestamp(),
          });
          console.log("✅ Abonnement Gold activé");
          
          localStorage.removeItem("pendingPlan");
          setStatus("success");
          setMessage("Votre abonnement Gold a été activé avec succès !");
          return;
        }

        // Plan non reconnu
        console.error("❌ Plan non reconnu:", plan);
        setStatus("error");
        setMessage("Plan non reconnu");

      } catch (error) {
        console.error("❌ Erreur lors de la mise à jour:", error);
        console.error("   → Message:", error.message);
        console.error("   → Code:", error.code);
        
        setStatus("error");
        setMessage("Erreur lors de l'activation. Contactez le support.");
      }
    };
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        console.error("❌ Utilisateur non connecté");
        setStatus("error");
        setMessage("Veuillez vous reconnecter pour activer votre abonnement");
        
        localStorage.setItem("redirectAfterLogin", window.location.pathname + window.location.search);
        
        setTimeout(() => {
          navigate("/login");
        }, 3000);
        
        return;
      }

      console.log("✅ Utilisateur authentifié:", user.email);
      await processPayment(user);
    });

    return () => unsubscribe();
  }, [navigate, searchParams]);

  // Compte à rebours après succès
  useEffect(() => {
    if (status === "success") {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate("/dashboard");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [status, navigate]);

  // ==========================
  // 🎨 RENDU UI
  // ==========================

  if (status === "processing") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          
          <div className="mb-6">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Activation en cours...
          </h2>

          <p className="text-gray-600 mb-4">
            {message}
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              ⏳ Cette opération prend généralement moins de 5 secondes
            </p>
          </div>

        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          
          <div className="text-6xl mb-6 animate-bounce">🎉</div>

          <h2 className="text-3xl font-bold text-green-600 mb-4">
            Activation réussie !
          </h2>

          <p className="text-gray-700 mb-6">
            {message}
          </p>

          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
            <p className="text-blue-800 font-semibold mb-2">
              Redirection vers votre dashboard
            </p>
            <p className="text-blue-600 text-6xl font-bold">
              {countdown}
            </p>
          </div>

          <button
            onClick={() => navigate("/dashboard")}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition"
          >
            📊 Aller au Dashboard maintenant →
          </button>

          <div className="mt-6 bg-green-50 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              💚 Bienvenue dans ProjectBoost ! Profitez pleinement de votre abonnement.
            </p>
          </div>

        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
        
        <div className="text-6xl mb-6">⚠️</div>

        <h2 className="text-2xl font-bold text-red-600 mb-4">
          Une erreur est survenue
        </h2>

        <p className="text-gray-700 mb-6">
          {message}
        </p>

        <div className="space-y-3">
          <button
            onClick={() => navigate("/pricing")}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition"
          >
            Revenir aux offres
          </button>
          
          <button
            onClick={() => navigate("/dashboard")}
            className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-3 px-6 rounded-lg transition"
          >
            Aller au Dashboard
          </button>
        </div>

        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            💡 Si votre paiement a été effectué, contactez le support avec votre numéro de transaction.
          </p>
        </div>

      </div>
    </div>
  );
};

export default PaymentReturn;